// Presentation helpers. Nothing here makes a decision; it only renders one.

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "Unknown";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "Unknown";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

// Stripe amounts are in the currency's minor unit, so an invoice for $10.00
// arrives as 1000. Dividing by 100 is wrong for zero-decimal currencies, but we
// only ever charge USD -- stated here so the assumption is visible if that
// changes.
export function formatMoney(minorUnits: number, currency = "usd"): string {
  const amount = (Number(minorUnits) || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

// "5m ago", "3h ago", "Yesterday", "4d ago", then a date. For lists where
// how recent something is matters more than exactly when.
export function formatRelative(iso: string | null | undefined, now = Date.now()): string {
  // A missing or unreadable time gets the same placeholder formatDate uses.
  const then = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(then)) return formatDate(iso);
  const minutes = Math.floor((now - then) / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

// Every string in this app is rendered by React, which escapes automatically.
// The one exception is the embed snippet, which a customer copies out into
// their own HTML -- so the publicId is checked against the shape the server
// generates before it can be pasted into a <script> tag.
export function embedSnippet(scriptUrl: string, publicId: string): string {
  if (!/^[0-9A-Za-z]{1,64}$/.test(publicId)) return "";
  return `<script src="${scriptUrl}" data-agent="${publicId}"></script>`;
}

// Binary units, because that is what a file manager shows and a customer
// comparing our "19.1 MB" against their OS's should see the same number.
export function formatBytes(bytes: number): string {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
